import React, { useEffect, useState } from 'react';

const STATUS_REFRESH_INTERVAL_MS = 60 * 1000;
const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif)$/i;

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function getUploadError(data, fallback) {
  return data && typeof data.error === 'string' ? data.error : fallback;
}

function PhotoUpload() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploaderName, setUploaderName] = useState('');
  const [nameError, setNameError] = useState('');
  const [uploaderPhone, setUploaderPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    let active = true;

    const loadStatus = async () => {
      try {
        const response = await fetch('/api/photo-upload/status', { cache: 'no-store' });
        if (!response.ok) throw new Error('Failed to load photo upload status');
        const nextStatus = await response.json();
        if (active) setStatus(nextStatus.enabled ? nextStatus : null);
      } catch (error) {
        console.error('Error loading photo upload status:', error);
        if (active) setStatus(null);
      }
    };

    loadStatus();
    const intervalId = window.setInterval(loadStatus, STATUS_REFRESH_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, []);

  if (!status) return null;

  const updateItem = (id, changes) => {
    setItems((current) => current.map((item) => (
      item.id === id ? { ...item, ...changes } : item
    )));
  };

  const onFilesSelected = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const maxFileSizeBytes = status.maxFileSizeMb * 1024 * 1024;

    setMessage('');
    if (selectedFiles.length > status.maxFiles) {
      setItems([]);
      setMessage(`사진은 한 번에 최대 ${status.maxFiles}장까지 선택할 수 있습니다.`);
      event.target.value = '';
      return;
    }

    const invalidType = selectedFiles.find((file) => (
      !(status.allowedTypes.includes(file.type) || ACCEPTED_EXTENSIONS.test(file.name))
    ));
    if (invalidType) {
      setItems([]);
      setMessage(`${invalidType.name}: 지원하지 않는 사진 형식입니다.`);
      event.target.value = '';
      return;
    }

    const oversized = selectedFiles.find((file) => file.size > maxFileSizeBytes);
    if (oversized) {
      setItems([]);
      setMessage(`${oversized.name}: 사진 한 장은 ${status.maxFileSizeMb}MB 이하여야 합니다.`);
      event.target.value = '';
      return;
    }

    setItems(selectedFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      file,
      state: 'ready',
      detail: '',
    })));
    event.target.value = '';
  };

  const uploadPhotos = async () => {
    const targets = items.filter((item) => item.state !== 'success');
    if (targets.length === 0 || isUploading) return;

    const name = uploaderName.trim().normalize('NFC');
    if (!name || name.length > 50 || /[<>:"/\\|?*]/.test(name)
      || Array.from(name).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
      || /[. ]$/.test(name) || /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(name)) {
      setNameError('이름을 50자 이내로 입력해주세요. 특수문자와 끝의 마침표는 사용할 수 없습니다.');
      document.getElementById('guest-photo-name')?.focus();
      return;
    }

    setNameError('');
    const phone = uploaderPhone.replace(/[\s-]/g, '');
    if (uploaderPhone.length > 20 || !/^01[016789]\d{7,8}$/.test(phone)) {
      setPhoneError('올바른 핸드폰 번호를 입력해주세요. 예: 010-1234-5678');
      document.getElementById('guest-photo-phone')?.focus();
      return;
    }
    setPhoneError('');
    setIsUploading(true);
    setMessage('');
    let successCount = 0;

    for (let index = 0; index < targets.length; index += 1) {
      const item = targets[index];
      updateItem(item.id, {
        state: 'uploading',
        detail: `${index + 1}/${targets.length} 업로드 중`,
      });

      try {
        const formData = new FormData();
        formData.append('uploaderName', name);
        formData.append('uploaderPhone', phone);
        formData.append('photo', item.file);
        const response = await fetch('/api/photos', {
          method: 'POST',
          body: formData,
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (response.status === 413) {
            throw new Error(`사진 한 장은 ${status.maxFileSizeMb}MB 이하여야 합니다.`);
          }
          if (response.status === 429) {
            throw new Error('업로드 요청이 많습니다. 잠시 후 다시 시도해주세요.');
          }
          if (response.status === 507) {
            throw new Error('서버의 사진 저장 공간이 부족합니다.');
          }
          throw new Error(getUploadError(data, '사진 업로드에 실패했습니다.'));
        }

        successCount += 1;
        updateItem(item.id, { state: 'success', detail: '업로드 완료' });
      } catch (error) {
        updateItem(item.id, {
          state: 'error',
          detail: error.message || '사진 업로드에 실패했습니다.',
        });
      }
    }

    setIsUploading(false);
    if (successCount === targets.length) {
      setMessage('소중한 사진을 공유해주셔서 감사합니다.');
    } else if (successCount > 0) {
      setMessage('일부 사진을 올리지 못했습니다. 실패한 사진을 다시 시도해주세요.');
    } else {
      setMessage('사진을 올리지 못했습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const pendingCount = items.filter((item) => item.state !== 'success').length;

  return (
    <section className="photo-upload container" aria-labelledby="photo-upload-title">
      <div className="photo-upload__badge">Wedding Day</div>
      <h2 className="photo-upload__title" id="photo-upload-title">
        오늘의 순간을 공유해주세요
      </h2>
      <p className="photo-upload__description">
        두 사람의 특별한 순간을 사진으로 남겨주세요.
      </p>

      <div className="photo-upload__name-field">
        <label className="photo-upload__name-label" htmlFor="guest-photo-name">
          사진 올리는 분의 이름 <span>(필수)</span>
        </label>
        <input
          className="photo-upload__name-input"
          id="guest-photo-name"
          type="text"
          autoComplete="name"
          placeholder="이름을 입력해주세요"
          maxLength={50}
          required
          value={uploaderName}
          disabled={isUploading}
          aria-invalid={Boolean(nameError)}
          aria-describedby="guest-photo-name-error"
          onChange={(event) => {
            setUploaderName(event.target.value);
            setNameError('');
          }}
        />
        <p className="photo-upload__name-error" id="guest-photo-name-error" role="alert">
          {nameError}
        </p>
      </div>

      <div className="photo-upload__name-field">
        <label className="photo-upload__name-label" htmlFor="guest-photo-phone">
          핸드폰 번호 <span>(필수)</span>
        </label>
        <input
          className="photo-upload__name-input"
          id="guest-photo-phone"
          type="tel"
          autoComplete="tel-national"
          placeholder="010-1234-5678"
          maxLength={20}
          required
          value={uploaderPhone}
          disabled={isUploading}
          aria-invalid={Boolean(phoneError)}
          aria-describedby="guest-photo-phone-error"
          onChange={(event) => {
            setUploaderPhone(event.target.value);
            setPhoneError('');
          }}
        />
        <p className="photo-upload__name-error" id="guest-photo-phone-error" role="alert">
          {phoneError}
        </p>
      </div>

      <input
        className="photo-upload__input"
        id="guest-photo-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        multiple
        disabled={isUploading}
        onChange={onFilesSelected}
      />
      <label className="photo-upload__picker" htmlFor="guest-photo-input">
        사진 선택하기
      </label>
      <div className="photo-upload__limit">
        한 번에 최대 {status.maxFiles}장 · 장당 {status.maxFileSizeMb}MB
      </div>

      {items.length > 0 && (
        <ul className="photo-upload__files" aria-label="선택한 사진">
          {items.map((item) => (
            <li className={`photo-upload__file photo-upload__file--${item.state}`} key={item.id}>
              <div className="photo-upload__file-info">
                <span className="photo-upload__file-name">{item.file.name}</span>
                <span>{formatFileSize(item.file.size)}</span>
              </div>
              {item.detail && <div className="photo-upload__file-status">{item.detail}</div>}
            </li>
          ))}
        </ul>
      )}

      {pendingCount > 0 && (
        <button
          className="photo-upload__submit"
          type="button"
          disabled={isUploading}
          onClick={uploadPhotos}
        >
          {isUploading
            ? '사진을 올리고 있어요…'
            : items.some((item) => item.state === 'error')
              ? '실패한 사진 다시 올리기'
              : '사진 올리기'}
        </button>
      )}

      {message && (
        <p className="photo-upload__message" role="status" aria-live="polite">
          {message}
        </p>
      )}
      <p className="photo-upload__privacy">
        사진의 촬영 정보 및 위치 정보가 원본 그대로 저장될 수 있습니다.
      </p>
    </section>
  );
}

export default PhotoUpload;
